import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #freeShippingThreshold = 2990;

  /** @type {number} */
  #reservationDurationSeconds = 600;

  /** @type {number | null} */
  #reservationTimerInterval = null;

  /**
   * Clave de sessionStorage donde se guarda el timestamp (ms epoch) en el
   * que vence la reserva. Así el countdown sigue corriendo en tiempo real
   * aunque se cierre/reabra el drawer o se navegue a otra página — solo
   * arranca de nuevo en 10:00 cuando el carrito se vació (nueva reserva) o
   * cuando se cierra la pestaña (sessionStorage se borra solo).
   * @type {string}
   */
  #reservationStorageKey = 'jv-cart-reservation-expires-at';

  /**
   * Handles del carrusel de upsell "Completá tu stack", en el orden en que
   * se muestran (2 por página → Caravanas, Collares, Pulseras).
   * @type {string[]}
   */
  #upsellHandles = ['cyber-kiss', 'blade-heart', 't-bar-chain', 'classic-grip', 'cuban-link', 'steel-rosary'];

  /**
   * Handles con descuento CONDICIONAL ("comprá otro accesorio y esto sale
   * -X%"). Estos productos no bajan de precio en /products/{handle}.js (el
   * descuento es un Buy X Get Y que solo se aplica en el carrito), así que
   * el % de acá tiene que coincidir siempre con el % configurado en el
   * descuento automático de Shopify correspondiente ("Stack -50%" para
   * caravanas/pulseras, "Stack -30%" para cadenas). Si cambiás el % en
   * Shopify, cambialo también acá.
   * @type {Record<string, number>}
   */
  #bundleDiscountPercentages = {
    't-bar-chain': 0.5,
    'classic-grip': 0.5,
    'cyber-kiss': 0.5,
    'blade-heart': 0.5,
    'cuban-link': 0.3,
    'steel-rosary': 0.3,
  };

  /** @type {number} */
  #upsellPageSize = 2;

  /** @type {Array<{handle: string, title: string, image: string, price: string | null, discountedPrice: string, badge: string | null, variantId: number}> | null} */
  #upsellProductsCache = null;

  /** @type {boolean} */
  #isAddingUpsellItem = false;

  /** @type {number | null} */
  #upsellAutoplayTimer = null;

  /** @type {number} */
  #upsellAutoplayDelay = 4000;

  /** @type {number} */
  #upsellCurrentPage = 0;

  /** @type {number} */
  #upsellTotalPages = 0;

  /** @type {boolean} */
  #isProgrammaticUpsellScroll = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#renderReservationBar);
    this.addEventListener(DialogOpenEvent.eventName, this.#renderFreeShippingBar);
    this.addEventListener(DialogOpenEvent.eventName, this.#renderUpsellCarousel);
    this.addEventListener(DialogOpenEvent.eventName, this.#resumeUpsellAutoplay);
    this.addEventListener(DialogOpenEvent.eventName, this.#renderPaymentBadges);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopUpsellAutoplay);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopReservationTimer);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    // Observar cambios en el carrito para actualizar la barra
    this.#observeCartChanges();
    this.#applyCheckoutButtonStyles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#renderReservationBar);
    this.removeEventListener(DialogOpenEvent.eventName, this.#renderFreeShippingBar);
    this.removeEventListener(DialogOpenEvent.eventName, this.#renderUpsellCarousel);
    this.removeEventListener(DialogOpenEvent.eventName, this.#resumeUpsellAutoplay);
    this.removeEventListener(DialogOpenEvent.eventName, this.#renderPaymentBadges);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopUpsellAutoplay);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopReservationTimer);
    this.#historyAbortController?.abort();
    this.#stopUpsellAutoplay();
    this.#stopReservationTimer();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
    this.#renderFreeShippingBar();
    this.#renderUpsellCarousel();
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * Contenedor fijo antes de `.cart-drawer__items` donde viven, en orden,
   * la barra de reserva y la de envío gratis. Insertarlas siempre DENTRO de
   * este contenedor (en vez de como hermanas sueltas ancladas entre sí) evita
   * que el orden final dependa de cuál de las dos terminó de renderizar primero.
   * @param {HTMLDialogElement} dialog
   */
  #ensureTopBarsContainer(dialog) {
    const existing = dialog.querySelector('.cart-drawer__top-bars');
    if (existing) return existing;

    const drawerItems = dialog.querySelector('.cart-drawer__items');
    if (!drawerItems) return null;

    drawerItems.insertAdjacentHTML('beforebegin', '<div class="cart-drawer__top-bars"></div>');
    return dialog.querySelector('.cart-drawer__top-bars');
  }

  /**
   * Renderiza la barra de "carrito reservado" con cuenta regresiva. El
   * vencimiento real vive en sessionStorage (#getReservationRemainingSeconds),
   * así que cerrar y reabrir el drawer (o navegar a otra página) NO reinicia
   * el countdown a 10:00 — sigue contando desde donde iba. Solo arranca una
   * reserva nueva cuando el carrito está vacío (ahí se cancela cualquier
   * reserva pendiente) o cuando la anterior ya venció.
   *
   * Se inserta siempre como primer hijo de #ensureTopBarsContainer, así queda
   * arriba de la barra de envío gratis sin importar cuál de las dos renderizó
   * primero.
   */
  #renderReservationBar = () => {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
    if (!dialog) return;

    const cartTotal = this.#getCartTotal();
    if (cartTotal === 0) {
      // Carrito vacío: cancelar cualquier reserva pendiente para que la
      // próxima vez que se agregue algo arranque una cuenta nueva de 10:00.
      sessionStorage.removeItem(this.#reservationStorageKey);
      this.#stopReservationTimer();
      return;
    }

    const container = this.#ensureTopBarsContainer(dialog);
    if (!container) return;

    // Ya está montada (venimos de cerrar/reabrir el drawer): no tocarla, solo
    // asegurarse de que el intervalo siga corriendo.
    if (container.querySelector('.cart-drawer__reservation-bar')) {
      if (this.#reservationTimerInterval === null) {
        const remainingSeconds = this.#getReservationRemainingSeconds();
        if (remainingSeconds <= 0) {
          container.querySelector('.cart-drawer__reservation-bar')?.remove();
          return;
        }
        this.#startReservationTimer(dialog, remainingSeconds);
      }
      return;
    }

    const remainingSeconds = this.#getReservationRemainingSeconds();
    if (remainingSeconds <= 0) return;

    const clockIcon = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 3"></path></svg>`;

    const barHTML = `
      <div class="cart-drawer__reservation-bar">
        <span class="reservation-bar__icon">${clockIcon}</span>
        <span class="reservation-bar__text">Carrito reservado por <strong class="reservation-bar__time">10:00</strong></span>
      </div>
    `;

    // 'afterbegin' del contenedor: siempre primero, sin importar qué otra
    // barra ya esté ahí adentro.
    container.insertAdjacentHTML('afterbegin', barHTML);
    this.#applyReservationStyles();
    this.#startReservationTimer(dialog, remainingSeconds);
  };

  /**
   * Devuelve cuántos segundos quedan de reserva, leyendo (o creando, si no
   * hay una vigente) el timestamp de vencimiento guardado en sessionStorage.
   */
  #getReservationRemainingSeconds() {
    const now = Date.now();
    const stored = Number(sessionStorage.getItem(this.#reservationStorageKey));
    let expiresAt = stored && stored > now ? stored : null;

    if (!expiresAt) {
      expiresAt = now + this.#reservationDurationSeconds * 1000;
      sessionStorage.setItem(this.#reservationStorageKey, String(expiresAt));
    }

    return Math.round((expiresAt - now) / 1000);
  }

  /**
   * Arranca la cuenta regresiva de la barra de reserva y la actualiza cada
   * segundo a partir de los segundos restantes reales (no siempre desde
   * #reservationDurationSeconds).
   * @param {HTMLDialogElement} dialog
   * @param {number} remainingSeconds
   */
  #startReservationTimer(dialog, remainingSeconds) {
    const timeEl = dialog.querySelector('.reservation-bar__time');
    if (!timeEl) return;

    let remaining = remainingSeconds;

    const tick = () => {
      if (remaining <= 0) {
        this.#stopReservationTimer();
        sessionStorage.removeItem(this.#reservationStorageKey);
        dialog.querySelector('.cart-drawer__reservation-bar')?.remove();
        return;
      }

      const minutes = Math.floor(remaining / 60).toString().padStart(2, '0');
      const seconds = (remaining % 60).toString().padStart(2, '0');
      timeEl.textContent = `${minutes}:${seconds}`;
      remaining -= 1;
    };

    tick();
    this.#reservationTimerInterval = window.setInterval(tick, 1000);
  }

  /**
   * Detiene el intervalo de la barra de reserva (al cerrar el drawer). No
   * borra el vencimiento guardado — por eso al reabrir sigue desde donde iba
   * en vez de reiniciar a 10:00.
   */
  #stopReservationTimer = () => {
    if (this.#reservationTimerInterval !== null) {
      window.clearInterval(this.#reservationTimerInterval);
      this.#reservationTimerInterval = null;
    }
  };

  /**
   * Renderiza la barra de progreso para envío gratis
   */
  #renderFreeShippingBar = (forcedTotal) => {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
    if (!dialog || dialog.querySelector('.cart-drawer__free-shipping-bar')) return;

    // Total del carrito: usar el pasado (desde /cart.js) o leerlo del DOM.
    const cartTotal = typeof forcedTotal === 'number' ? forcedTotal : this.#getCartTotal();

    if (cartTotal === 0) return; // No mostrar si el carrito está vacío

    const remaining = Math.max(0, this.#freeShippingThreshold - cartTotal);
    const progressPercentage = Math.min((cartTotal / this.#freeShippingThreshold) * 100, 100);
    const isUnlocked = remaining <= 0;

    const truckIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M1 4h13v11H1z"/><path d="M14 8h4l3 3v4h-7"/><circle cx="5" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>`;
    const checkIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>`;

    const barHTML = `
      <div class="cart-drawer__free-shipping-bar${isUnlocked ? ' is-unlocked' : ''}">
        <div class="free-shipping-message">
          <span class="free-shipping-message__icon">${isUnlocked ? checkIcon : truckIcon}</span>
          <span class="free-shipping-message__text">
            ${isUnlocked
              ? `¡Tenés <strong>envío gratis</strong>!`
              : `AÑADÍ <strong>$${Math.ceil(remaining)}</strong> Y CONSEGUÍ ENVÍO GRATIS`
            }
          </span>
        </div>
        <div class="free-shipping-progress">
          <div class="free-shipping-progress__bar" style="width: ${progressPercentage}%"></div>
        </div>
      </div>
    `;

    // Se agrega al final de #ensureTopBarsContainer: si la barra de reserva
    // ya está (o llega después), #renderReservationBar la fuerza siempre al
    // frente con 'afterbegin', así que acá simplemente appendeamos.
    const container = this.#ensureTopBarsContainer(dialog);
    if (!container) return;

    container.insertAdjacentHTML('beforeend', barHTML);
    this.#applyFreeShippingStyles();
  };

  /**
   * Obtiene el total del carrito en UYU (sin centavos)
   */
  #getCartTotal() {
    // Buscar el elemento que contiene el total
    const cartTotalElement = document.querySelector('[data-cart-subtotal]');
    if (!cartTotalElement) return 0;

    const totalText = cartTotalElement.textContent.trim();
    // Extraer solo números y comas
    const cleanedTotal = totalText.replace(/[^\d,]/g, '').replace(',', '.');
    return parseFloat(cleanedTotal) || 0;
  }

  /**
   * ID de la sección de Shopify que contiene el cart drawer.
   */
  #getCartSectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '') ?? null;
  }

  /**
   * Refresca solo la lista de items del drawer con la Section Rendering API,
   * sin reemplazar el <dialog> (no rompe el estado abierto ni la animación).
   * @param {string | null} sectionId
   */
  #refreshCartDrawerItems = async (sectionId) => {
    if (!sectionId) return;
    try {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`;
      const data = await fetch(url).then((r) => r.json());
      const html = data[sectionId];
      if (!html) return;

      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
      const newItems = fresh.querySelector('.cart-drawer__items');
      const currentItems = dialog?.querySelector('.cart-drawer__items');
      if (newItems && currentItems) currentItems.replaceWith(newItems);

      // El total ("Total estimado") vive en `.cart-totals`, un hermano de
      // `.cart-drawer__items` dentro de `.cart-drawer__summary` — sin esto,
      // agregar un producto del carrusel de upsell suma el ítem al carrito
      // pero deja el total estimado desactualizado. `.cart-totals` es hermano
      // de `.cart__ctas` (donde viven el carrusel y las badges de pago
      // inyectados por JS), así que reemplazarlo no los afecta.
      const newTotals = fresh.querySelector('.cart-totals');
      const currentTotals = dialog?.querySelector('.cart-totals');
      if (newTotals && currentTotals) currentTotals.replaceWith(newTotals);
    } catch (error) {
      console.error('No se pudo refrescar el carrito:', error);
    }
  };

  /**
   * Obtiene el variant ID de un producto por handle
   */
  #getProductVariantId = async (handle) => {
    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();
      return product.variants[0]?.id || null;
    } catch (error) {
      console.error('Error fetching product variant:', error);
      return null;
    }
  };

  /**
   * Observar cambios en el carrito para actualizar la barra
   */
  #observeCartChanges = () => {
    const observer = new MutationObserver(() => {
      const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
      if (dialog?.open) {
        // Remover barra anterior
        dialog.querySelector('.cart-drawer__free-shipping-bar')?.remove();
        // Renderizar nueva
        this.#renderFreeShippingBar();
      }
    });

    // Observar cambios en el contenedor de items
    const cartItemsContainer = document.querySelector('[data-cart-subtotal]');
    if (cartItemsContainer) {
      observer.observe(cartItemsContainer, {
        characterData: true,
        subtree: true,
        childList: true
      });
    }
  };

  /**
   * Aplica estilos CSS para la barra de "carrito reservado"
   */
  #applyReservationStyles() {
    if (document.getElementById('reservation-bar-styles')) return;

    const style = document.createElement('style');
    style.id = 'reservation-bar-styles';
    style.textContent = `
      .cart-drawer__reservation-bar {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        background: #f5f5f5;
        border-bottom: 1px solid #ececec;
        padding: 9px 16px;
        margin: 0;
      }

      .reservation-bar__icon {
        display: inline-flex;
        flex-shrink: 0;
        width: 13px;
        height: 13px;
        color: #1a1a1a;
      }

      .reservation-bar__icon svg {
        width: 100%;
        height: 100%;
      }

      .reservation-bar__text {
        font-family: 'Helvetica Neue', Helvetica, sans-serif;
        font-size: 11.5px;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: #1a1a1a;
        white-space: nowrap;
      }

      .reservation-bar__time {
        color: #1a1a1a;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Aplica estilos CSS para la barra de envío gratis
   */
  #applyFreeShippingStyles() {
    if (document.getElementById('free-shipping-styles')) return;

    const style = document.createElement('style');
    style.id = 'free-shipping-styles';
    style.textContent = `
      .cart-drawer__free-shipping-bar {
        background: transparent;
        border: none;
        border-radius: 0;
        padding: 10px 16px 12px;
        margin: 0;
        box-shadow: none;
      }

      .free-shipping-message {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        font-family: 'Helvetica Neue', Helvetica, sans-serif;
        font-size: 11.5px;
        font-weight: 500;
        color: #1a1a1a;
        text-align: center;
        margin-bottom: 6px;
        letter-spacing: 0.15px;
      }

      .free-shipping-message__icon {
        display: inline-flex;
        flex-shrink: 0;
        width: 14px;
        height: 14px;
        color: #1a1a1a;
      }

      .free-shipping-message__icon svg {
        width: 100%;
        height: 100%;
      }

      .free-shipping-message strong {
        color: #000000;
        font-weight: 700;
      }

      .free-shipping-progress {
        position: relative;
        width: 100%;
        height: 4px;
        background: #ededed;
        border-radius: 100px;
        overflow: hidden;
      }

      .free-shipping-progress__bar {
        position: relative;
        height: 100%;
        background: linear-gradient(90deg, #1a1a1a 0%, #3f352b 100%);
        border-radius: 100px;
        transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;
      }

      .free-shipping-progress__bar::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
        transform: translateX(-100%);
        animation: free-shipping-shine 2.2s ease-in-out infinite;
      }

      @keyframes free-shipping-shine {
        0% { transform: translateX(-100%); }
        60%, 100% { transform: translateX(100%); }
      }

      .cart-drawer__free-shipping-bar.is-unlocked .free-shipping-message,
      .cart-drawer__free-shipping-bar.is-unlocked .free-shipping-message__icon {
        color: #2e7d32;
      }

      .cart-drawer__free-shipping-bar.is-unlocked .free-shipping-progress__bar {
        background: linear-gradient(90deg, #2e7d32 0%, #43a047 100%);
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Renderiza el carrusel de upsell "Completá tu stack"
   */
  #renderUpsellCarousel = async () => {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
    if (!dialog || dialog.querySelector('.cart-drawer__upsell-carousel')) return;

    const products = await this.#getUpsellProducts();
    if (!products.length) return;

    const pages = [];
    for (let i = 0; i < products.length; i += this.#upsellPageSize) {
      pages.push(products.slice(i, i + this.#upsellPageSize));
    }

    this.#upsellTotalPages = pages.length;
    this.#upsellCurrentPage = 0;

    const dotsHTML =
      pages.length > 1
        ? `<div class="upsell-carousel__dots">${pages
            .map(
              (_, i) =>
                `<button class="upsell-carousel__dot${i === 0 ? ' is-active' : ''}" type="button" data-page="${i}" aria-label="Ver página ${i + 1}"></button>`
            )
            .join('')}</div>`
        : '';

    const pagesHTML = pages
      .map(
        (pageItems, pageIndex) => `
          <div class="upsell-carousel__page" data-page="${pageIndex}">
            ${pageItems.map((product) => this.#renderUpsellItem(product)).join('')}
          </div>
        `
      )
      .join('');

    const carouselHTML = `
      <div class="cart-drawer__upsell-carousel">
        <div class="upsell-carousel__header">
          <span class="upsell-carousel__title">Completá tu stack</span>
          ${dotsHTML}
        </div>
        <div class="upsell-carousel__track-wrap">
          <div class="upsell-carousel__track">
            ${pagesHTML}
          </div>
        </div>
      </div>
    `;

    // Pegado arriba del botón de pagar (en vez de antes del resumen): así es
    // lo último que el usuario ve justo antes de confirmar la compra, lo que
    // aumenta la probabilidad de que agregue algo del stack antes de pagar.
    const checkoutButton = dialog.querySelector('button[name="checkout"]');
    if (checkoutButton && !dialog.querySelector('.cart-drawer__upsell-carousel')) {
      checkoutButton.insertAdjacentHTML('beforebegin', carouselHTML);
      this.#applyUpsellCarouselStyles();
      this.#attachUpsellCarouselListeners(dialog);
      this.#startUpsellAutoplay(dialog);
    }
  };

  /**
   * Markup de un item individual del carrusel de upsell
   * @param {{handle: string, title: string, image: string, price: string | null, discountedPrice: string, badge: string | null, variantId: number}} product
   */
  #renderUpsellItem(product) {
    return `
      <div class="upsell-item" data-variant-id="${product.variantId}">
        <a href="/products/${product.handle}" class="upsell-item__img-link">
          <img class="upsell-item__img" src="${product.image}" alt="${product.title}" width="48" height="48" loading="lazy">
        </a>
        <div class="upsell-item__info">
          <a href="/products/${product.handle}" class="upsell-item__name">${product.title}</a>
          ${product.badge ? `<span class="upsell-item__badge">${product.badge}</span>` : ''}
          <div class="upsell-item__pricing">
            <span class="upsell-item__price upsell-item__price--sale">$${product.discountedPrice}</span>
            ${product.price ? `<span class="upsell-item__price upsell-item__price--compare">$${product.price}</span>` : ''}
          </div>
        </div>
        <button class="upsell-item__add" type="button" data-variant-id="${product.variantId}" aria-label="Agregar ${product.title}">
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5.5" y1="1" x2="5.5" y2="10"></line><line x1="1" y1="5.5" x2="10" y2="5.5"></line></svg>
        </button>
      </div>
    `;
  }

  /**
   * Obtiene (y cachea) los datos de los productos del carrusel de upsell
   * directo de Shopify vía /products/{handle}.js — imagen, precio, "antes" y
   * variant ID reales.
   *
   * Dos casos, según el handle:
   * - Precio plano: el precio final YA está seteado en Shopify (variant.price)
   *   con su compare_at_price. Se muestra tal cual viene, nunca puede
   *   desincronizarse de lo que se cobra.
   * - Descuento condicional (handles en #bundleDiscountPercentages): estos
   *   productos mantienen su precio normal en Shopify — el descuento es un
   *   Buy X Get Y que solo se aplica en el carrito, no existe como
   *   compare_at_price en /products/{handle}.js. Acá se calcula el precio
   *   mostrado para que coincida con ese descuento.
   *
   * Filtra productos sin stock (available === false) para no mostrar tarjetas
   * que no se pueden comprar.
   */
  #getUpsellProducts = async () => {
    if (this.#upsellProductsCache) return this.#upsellProductsCache;

    const results = await Promise.all(
      this.#upsellHandles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) return null;
          const product = await response.json();
          const variant = product.variants?.[0];
          if (!variant || !variant.available) return null;

          const rawImage = variant.featured_image?.url ?? product.featured_image ?? null;
          const image = rawImage ? `${rawImage}${rawImage.includes('?') ? '&' : '?'}width=200` : '';

          const bundlePercentage = this.#bundleDiscountPercentages[handle] ?? null;

          let price;
          let discountedPrice;
          let badge;

          if (bundlePercentage) {
            // Descuento condicional: precio normal tachado, calculado -X% para mostrar.
            const discountedCents = Math.round(variant.price * (1 - bundlePercentage));
            price = (variant.price / 100).toFixed(2).replace('.', ',');
            discountedPrice = (discountedCents / 100).toFixed(2).replace('.', ',');
            badge = `-${Math.round(bundlePercentage * 100)}% con otro accesorio`;
          } else {
            // Precio plano: leer compare_at_price real de Shopify si existe.
            const compareAtPrice =
              variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : null;
            const discountPercentage = compareAtPrice
              ? Math.round((1 - variant.price / compareAtPrice) * 100)
              : null;
            price = compareAtPrice ? (compareAtPrice / 100).toFixed(2).replace('.', ',') : null;
            discountedPrice = (variant.price / 100).toFixed(2).replace('.', ',');
            badge = discountPercentage ? `-${discountPercentage}%` : null;
          }

          return {
            handle,
            title: product.title,
            image,
            price,
            discountedPrice,
            badge,
            variantId: variant.id,
          };
        } catch (error) {
          console.error(`Error fetching upsell product ${handle}:`, error);
          return null;
        }
      })
    );

    this.#upsellProductsCache = results.filter((product) => product !== null);
    return this.#upsellProductsCache;
  };

  /**
   * Adjunta listeners a los dots y botones de agregar del carrusel
   * @param {HTMLDialogElement} dialog
   */
  #attachUpsellCarouselListeners(dialog) {
    const carousel = dialog.querySelector('.cart-drawer__upsell-carousel');
    if (!carousel || carousel.dataset.bound === 'true') return;
    carousel.dataset.bound = 'true';

    const trackWrap = /** @type {HTMLElement | null} */ (carousel.querySelector('.upsell-carousel__track-wrap'));

    carousel.querySelectorAll('.upsell-carousel__dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        const page = Number(/** @type {HTMLElement} */ (dot).dataset.page);
        this.#goToUpsellPage(carousel, page);
        // Reiniciar el auto-avance para que no salte justo después de un click manual.
        this.#startUpsellAutoplay(dialog);
      });
    });

    carousel.querySelectorAll('.upsell-item__add').forEach((button) => {
      button.addEventListener('click', this.#handleUpsellAdd);
    });

    // Pausar mientras el mouse está sobre el carrusel (no queremos que se
    // mueva la tarjeta justo cuando el usuario va a tocar "agregar").
    carousel.addEventListener('mouseenter', this.#stopUpsellAutoplay);
    carousel.addEventListener('mouseleave', () => this.#startUpsellAutoplay(dialog));

    // Swipe/scroll manual (mobile): el snap de por sí lo maneja el navegador
    // vía CSS (scroll-snap-type), acá solo escuchamos dónde quedó al soltar
    // para sincronizar puntitos + contador, y pausamos el auto-avance mientras
    // el usuario está arrastrando para que no compitan entre sí.
    if (trackWrap) {
      let scrollSettleTimer;
      trackWrap.addEventListener(
        'scroll',
        () => {
          if (!this.#isProgrammaticUpsellScroll) this.#stopUpsellAutoplay();

          window.clearTimeout(scrollSettleTimer);
          scrollSettleTimer = window.setTimeout(() => {
            if (this.#isProgrammaticUpsellScroll) {
              this.#isProgrammaticUpsellScroll = false;
              return;
            }
            const page = Math.round(trackWrap.scrollLeft / trackWrap.clientWidth);
            this.#syncUpsellDots(carousel, page);
            this.#startUpsellAutoplay(dialog);
          }, 120);
        },
        { passive: true }
      );
    }
  }

  /**
   * Marca como activo el punto de la página indicada y actualiza el contador
   * interno, sin disparar ningún scroll (para sincronizar tras un swipe manual).
   * @param {Element} carousel
   * @param {number} page
   */
  #syncUpsellDots(carousel, page) {
    this.#upsellCurrentPage = page;
    carousel.querySelectorAll('.upsell-carousel__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === page);
    });
  }

  /**
   * Navega el carrusel a una página específica con scroll nativo (respeta el
   * scroll-snap y anima suave), y sincroniza puntitos/contador al toque.
   * @param {Element} carousel
   * @param {number} page
   * @param {ScrollBehavior} [behavior]
   */
  #goToUpsellPage(carousel, page, behavior = 'smooth') {
    const trackWrap = /** @type {HTMLElement | null} */ (carousel.querySelector('.upsell-carousel__track-wrap'));
    if (!trackWrap) return;
    this.#isProgrammaticUpsellScroll = true;
    trackWrap.scrollTo({ left: page * trackWrap.clientWidth, behavior });
    this.#syncUpsellDots(carousel, page);
  }

  /**
   * Arranca (o reinicia) el auto-avance del carrusel de upsell cada
   * #upsellAutoplayDelay ms. No hace nada si hay una sola página.
   * @param {HTMLDialogElement} dialog
   */
  #startUpsellAutoplay = (dialog) => {
    this.#stopUpsellAutoplay();
    if (this.#upsellTotalPages <= 1) return;

    this.#upsellAutoplayTimer = window.setInterval(() => {
      const carousel = dialog.querySelector('.cart-drawer__upsell-carousel');
      if (!carousel) return;
      const nextPage = (this.#upsellCurrentPage + 1) % this.#upsellTotalPages;
      this.#goToUpsellPage(carousel, nextPage);
    }, this.#upsellAutoplayDelay);
  };

  /**
   * Detiene el auto-avance del carrusel (al cerrar el drawer, al hacer hover,
   * o antes de reiniciarlo).
   */
  #stopUpsellAutoplay = () => {
    if (this.#upsellAutoplayTimer !== null) {
      window.clearInterval(this.#upsellAutoplayTimer);
      this.#upsellAutoplayTimer = null;
    }
  };

  /**
   * Reanuda el auto-avance al reabrir el drawer, si el carrusel ya existe
   * (se arma de forma async la primera vez en #renderUpsellCarousel, que
   * llama a #startUpsellAutoplay directamente al terminar de armarlo).
   */
  #resumeUpsellAutoplay = () => {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
    if (!dialog || !dialog.querySelector('.cart-drawer__upsell-carousel')) return;
    this.#startUpsellAutoplay(dialog);
  };

  /**
   * Agrega el producto del carrusel al carrito y refresca items + barra de envío gratis
   * @param {Event} event
   */
  #handleUpsellAdd = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (this.#isAddingUpsellItem || button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    this.#isAddingUpsellItem = true;
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`add.js respondió ${response.status}`);

      button.closest('.upsell-item')?.classList.add('upsell-item--added');

      const cart = await fetch('/cart.js').then((r) => r.json());
      const sectionId = this.#getCartSectionId();
      await this.#refreshCartDrawerItems(sectionId);

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      document.querySelector('.cart-drawer__free-shipping-bar')?.remove();
      this.#renderFreeShippingBar(cart.total_price / 100);
      this.#announceCartCount(cart.item_count);
    } catch (error) {
      console.error('Error agregando producto del carrusel de upsell:', error);
    } finally {
      window.setTimeout(() => {
        button.closest('.upsell-item')?.classList.remove('upsell-item--added');
        button.disabled = false;
        this.#isAddingUpsellItem = false;
      }, 1200);
    }
  };

  /**
   * Aplica estilos CSS para el carrusel de upsell
   */
  #applyUpsellCarouselStyles() {
    if (document.getElementById('upsell-carousel-styles')) return;

    const style = document.createElement('style');
    style.id = 'upsell-carousel-styles';
    style.textContent = `
      .cart-drawer__upsell-carousel {
        background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
        border: 1px solid #ececec;
        border-radius: 14px;
        padding: 14px 16px 12px;
        margin: 12px 0 16px 0;
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
      }

      .upsell-carousel__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;
      }

      .upsell-carousel__title {
        font-family: 'Helvetica Neue', Helvetica, sans-serif;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #1a1a1a;
      }

      .upsell-carousel__dots {
        display: flex;
        gap: 5px;
      }

      .upsell-carousel__dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.18);
        border: none;
        padding: 0;
        cursor: pointer;
        transition: background 0.25s ease, transform 0.25s ease;
      }

      .upsell-carousel__dot.is-active {
        background: #1a1a1a;
        transform: scale(1.3);
      }

      .upsell-carousel__track-wrap {
        border-radius: 10px;
        /* Scroll nativo con snap: el swipe/scroll manual queda a cargo del
           navegador (más fluido que animar un transform a mano) y el JS solo
           lee dónde quedó para sincronizar puntitos y auto-avance. */
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      .upsell-carousel__track-wrap::-webkit-scrollbar {
        display: none;
      }

      .upsell-carousel__track {
        display: flex;
      }

      .upsell-carousel__page {
        flex: 0 0 100%;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
        scroll-snap-align: start;
        scroll-snap-stop: always;
      }

      .upsell-item {
        display: flex;
        align-items: center;
        gap: 10px;
        background: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.06);
        border-radius: 10px;
        padding: 7px 9px;
        transition: border-color 0.25s ease;
      }

      .upsell-item__img-link {
        flex-shrink: 0;
        display: block;
      }

      .upsell-item__img {
        width: 48px;
        height: 48px;
        object-fit: cover;
        border-radius: 8px;
        background: #f0f0f0;
        display: block;
      }

      .upsell-item__info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .upsell-item__name {
        font-family: 'Helvetica Neue', Helvetica, sans-serif;
        font-size: 12.5px;
        font-weight: 600;
        color: #1a1a1a;
        text-decoration: none;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .upsell-item__badge {
        font-family: 'Helvetica Neue', Helvetica, sans-serif;
        font-size: 9.5px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #b5321c;
      }

      .upsell-item__pricing {
        display: flex;
        align-items: baseline;
        gap: 6px;
      }

      .upsell-item__price {
        font-family: 'Helvetica Neue', Helvetica, sans-serif;
        font-size: 12px;
        font-weight: 700;
        color: #1a1a1a;
      }

      .upsell-item__price--sale {
        color: #b5321c;
      }

      .upsell-item__price--compare {
        font-size: 10.5px;
        font-weight: 500;
        color: #a8a8a8;
        text-decoration: line-through;
      }

      .upsell-item__add {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #1a1a1a;
        border: none;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        transition: background 0.15s, transform 0.15s;
      }

      .upsell-item__add:hover:not(:disabled) {
        background: #333;
        transform: scale(1.08);
      }

      .upsell-item__add:disabled {
        cursor: default;
        opacity: 0.7;
      }

      .upsell-item--added {
        border-color: #2e7d32;
      }

      .upsell-item--added .upsell-item__add {
        background: #2e7d32;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Aplica estilos al botón Pagar: fondo negro, texto blanco, glow pulsante.
   * `button[name="checkout"]` es el selector nativo de Shopify (el form del
   * carrito siempre nombra así su submit de checkout), no depende del tema.
   */
  #applyCheckoutButtonStyles() {
    if (document.getElementById('checkout-glow-styles')) return;

    const style = document.createElement('style');
    style.id = 'checkout-glow-styles';
    style.textContent = `
      cart-drawer-component button[name="checkout"] {
        background: #0a0a0a !important;
        color: #ffffff !important;
        border: none !important;
        border-radius: 20px !important;
        position: relative;
        overflow: hidden;
        isolation: isolate;
        animation: jv-checkout-glow 2.6s ease-in-out infinite;
      }

      cart-drawer-component button[name="checkout"]::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(115deg, transparent 30%, rgba(255, 255, 255, 0.22) 50%, transparent 70%);
        transform: translateX(-120%);
        animation: jv-checkout-shine 2.6s ease-in-out infinite;
        pointer-events: none;
      }

      @keyframes jv-checkout-glow {
        0%, 100% {
          box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), 0 4px 14px rgba(0, 0, 0, 0.18);
        }
        50% {
          box-shadow: 0 0 16px 3px rgba(255, 255, 255, 0.28), 0 4px 18px rgba(0, 0, 0, 0.25);
        }
      }

      @keyframes jv-checkout-shine {
        0% { transform: translateX(-120%); }
        55%, 100% { transform: translateX(120%); }
      }

      /* Texto de "Los impuestos y los gastos de envío se calculan en la
         página de pago": ocupa una línea extra sin aportar nada antes del
         carrusel de upsell, se saca para que el resumen quede más limpio. */
      cart-drawer-component .cart-totals__tax-note {
        display: none !important;
      }

      /* Más énfasis visual en los puntos clave del drawer: título "Carrito",
         "Total estimado" + su monto, y el texto del botón Pagar. */
      cart-drawer-component .cart-drawer__heading {
        font-weight: 700 !important;
        font-size: 17px !important;
      }

      cart-drawer-component .cart-totals__total-label,
      cart-drawer-component .cart-totals__total-value {
        font-weight: 700 !important;
      }

      cart-drawer-component .cart-totals__total-value {
        font-size: 20px !important;
      }

      cart-drawer-component button[name="checkout"] .button-text {
        font-weight: 700 !important;
        font-size: 16px !important;
        text-transform: uppercase !important;
      }

      @media (prefers-reduced-motion: reduce) {
        cart-drawer-component button[name="checkout"],
        cart-drawer-component button[name="checkout"]::after {
          animation: none;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Renderiza el texto de confianza + los logos de medios de pago debajo del botón Pagar.
   */
  #renderPaymentBadges = () => {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
    if (!dialog || dialog.querySelector('.cart-drawer__payment-badges')) return;

    const checkoutButton = dialog.querySelector('button[name="checkout"]');
    if (!checkoutButton) return;

    const shieldIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5z"></path><path d="m9 12 2 2 4-4"></path></svg>`;

    const badgesHTML = `
      <div class="cart-drawer__payment-badges">
        <div class="payment-badges__trust">
          <span class="payment-badges__trust-icon">${shieldIcon}</span>
          <span class="payment-badges__trust-text">Compra 100% segura y protegida</span>
        </div>
        <img class="payment-badges__img" src="https://cdn.shopify.com/s/files/1/0685/2021/4817/files/TarjetasLogos.png?v=1781230590" alt="Medios de pago aceptados" loading="lazy">
      </div>
    `;

    checkoutButton.insertAdjacentHTML('afterend', badgesHTML);
    this.#applyPaymentBadgesStyles();
  };

  /**
   * Aplica estilos CSS para el texto de confianza + logos de medios de pago
   */
  #applyPaymentBadgesStyles() {
    if (document.getElementById('payment-badges-styles')) return;

    const style = document.createElement('style');
    style.id = 'payment-badges-styles';
    style.textContent = `
      .cart-drawer__payment-badges {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 12px 16px 4px;
      }

      .payment-badges__trust {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }

      .payment-badges__trust-icon {
        display: inline-flex;
        color: #8a8a8a;
      }

      .payment-badges__trust-icon svg {
        width: 14px;
        height: 14px;
      }

      .payment-badges__trust-text {
        font-family: 'Helvetica Neue', Helvetica, sans-serif;
        font-size: 10.5px;
        font-weight: 600;
        letter-spacing: 0.03em;
        color: #6b6b6b;
        text-align: center;
      }

      .payment-badges__img {
        max-width: 260px;
        width: 100%;
        height: auto;
        display: block;
      }
    `;
    document.head.appendChild(style);
  }

  open() {
    this.showDialog();
    this.#renderReservationBar();
    this.#renderFreeShippingBar();
    this.#renderUpsellCarousel();
    this.#resumeUpsellAutoplay();
    this.#renderPaymentBadges();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Con la barra de reserva + carrusel de upsell, el resumen real ocupa más de lo que
    // mide `.cart-drawer__summary` por sí solo. En vez de dejar que ese cálculo
    // active el modo "sticky" (resumen fijo, productos scrolleando en una cajita),
    // forzamos siempre scroll normal de todo el drawer.
    dialog.setAttribute('cart-summary-sticky', 'false');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
